import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { getPaymentOrder, getPaymentTransactions } from '../lib/api';
import { formatCurrency, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw } from 'lucide-react';

const statusBadgeClass = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (['paid', 'fulfilled', 'success', 'complete', 'completed'].includes(normalized)) {
    return 'bg-green-100 text-green-700 border-green-200';
  }
  if (['pending', 'processing', 'requires_action'].includes(normalized)) {
    return 'bg-amber-100 text-amber-700 border-amber-200';
  }
  if (['failed', 'expired', 'canceled', 'cancelled'].includes(normalized)) {
    return 'bg-red-100 text-red-700 border-red-200';
  }
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const extractOrder = (payload) => payload?.order || payload || null;

const extractTransactions = (payload) => {
  if (Array.isArray(payload?.transactions)) return payload.transactions;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
};

export function PaymentOrderDetailsPage() {
  const { orderId } = useParams();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);

  const loadOrder = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPaymentOrder(orderId);
      setOrder(extractOrder(response?.data));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-56" />
        <Card><CardContent className="p-6"><Skeleton className="h-40 w-full" /></CardContent></Card>
      </div>
    );
  }

  if (!order) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-slate-600">Order details unavailable.</p>
          <Button asChild variant="outline" className="mt-4">
            <Link to="/payments/history">Go to Payment History</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-order-details-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-jakarta font-bold text-slate-900">Order Details</h1>
          <p className="text-slate-500 mt-1">Track payment and fulfillment state for this order.</p>
        </div>
        <Button variant="outline" onClick={loadOrder}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg break-all">{order.order_id || orderId}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs text-slate-500 mb-1">Status</p>
              <Badge variant="outline" className={statusBadgeClass(order.status)}>{order.status || '-'}</Badge>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs text-slate-500 mb-1">Payment</p>
              <Badge variant="outline" className={statusBadgeClass(order.payment_status)}>{order.payment_status || '-'}</Badge>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-xs text-slate-500 mb-1">Fulfillment</p>
              <Badge variant="outline" className={statusBadgeClass(order.fulfillment_status)}>{order.fulfillment_status || '-'}</Badge>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">Item Type</p>
              <p className="text-sm font-medium text-slate-900">{order.item_type || '-'}</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">Item ID</p>
              <p className="text-sm font-medium text-slate-900 break-all">{order.item_id || '-'}</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">Agent ID</p>
              <p className="text-sm font-medium text-slate-900 break-all">{order.agent_id || '-'}</p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">Amount</p>
              <p className="text-sm font-medium text-slate-900">
                {order.amount !== undefined && order.amount !== null
                  ? formatCurrency(order.amount)
                  : '-'}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">Created</p>
              <p className="text-sm font-medium text-slate-900">
                {order.created_at ? formatDateTime(order.created_at) : '-'}
              </p>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <p className="text-xs text-slate-500">Checkout Session</p>
              <p className="text-sm font-medium text-slate-900 break-all">
                {order.session_id || order.checkout_session_id || order.stripe_session_id || '-'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button asChild variant="outline">
          <Link to="/payments/history">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Payment History
          </Link>
        </Button>
      </div>
    </div>
  );
}

export function PaymentHistoryPage() {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);

  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getPaymentTransactions();
      setTransactions(extractTransactions(response?.data));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  return (
    <div className="space-y-6" data-testid="payment-history-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-jakarta font-bold text-slate-900">Payment History</h1>
          <p className="text-slate-500 mt-1">All checkout transactions, with payment and fulfillment state.</p>
        </div>
        <Button variant="outline" onClick={loadTransactions}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}><CardContent className="p-6"><Skeleton className="h-20 w-full" /></CardContent></Card>
          ))}
        </div>
      ) : transactions.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center">
            <p className="text-slate-600 mb-4">No payment transactions found.</p>
            <Button asChild>
              <Link to="/buy-minutes">Buy Minutes</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {transactions.map((txn, index) => {
            const orderId = txn.order_id || txn.order?.order_id || txn.id;
            return (
              <Card key={orderId || index} className="border border-slate-200">
                <CardContent className="p-5">
                  <div className="grid gap-3 sm:grid-cols-4">
                    <div>
                      <p className="text-xs text-slate-500">Order</p>
                      <p className="text-sm font-medium text-slate-900 break-all">{orderId || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Created</p>
                      <p className="text-sm text-slate-900">{txn.created_at ? formatDateTime(txn.created_at) : '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Amount</p>
                      <p className="text-sm text-slate-900">
                        {txn.amount !== undefined && txn.amount !== null ? formatCurrency(txn.amount) : '-'}
                      </p>
                    </div>
                    <div className="flex sm:justify-end items-center">
                      {orderId ? (
                        <Button asChild variant="outline" size="sm">
                          <Link to={`/payment/orders/${orderId}`}>View Order</Link>
                        </Button>
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Badge variant="outline" className={statusBadgeClass(txn.status)}>status: {txn.status || '-'}</Badge>
                    <Badge variant="outline" className={statusBadgeClass(txn.payment_status)}>payment: {txn.payment_status || '-'}</Badge>
                    <Badge variant="outline" className={statusBadgeClass(txn.fulfillment_status)}>fulfillment: {txn.fulfillment_status || '-'}</Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
